/**
 * Placeholder da lista ecommerce (filtros agrupados + tabela por transação).
 * @module EcommercePatientsListSkeleton
 */

import {
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  statsTableContainerSx,
  statsTableHeadCellSx,
  statsTableSx,
} from "@/pages/adminInsights/utils/statsTableStyles";

const SKELETON_ROWS = 8;
const GROUPS = [
  { title: "Status dos documentos", cards: 3 },
  { title: "Liberações", cards: 2 },
  { title: "Concluiu a compra", cards: 1 },
  { title: "Todos", cards: 1 },
  { title: "Listas", cards: 2 },
];

function QuickStatsSkeleton() {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={1.5}
      alignItems="stretch"
    >
      {GROUPS.map((group, groupIndex) => (
        <Paper
          key={group.title}
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            p: 1,
            flex: { xs: "1 1 100%", sm: "1 1 auto" },
            minWidth: 0,
            bgcolor: "action.hover",
            borderColor: "divider",
            ...(groupIndex > 0 && {
              borderLeft: { md: "3px solid" },
              borderLeftColor: { md: "text.disabled" },
              pl: { md: 1.5 },
            }),
          }}
        >
          <Skeleton variant="text" width={120} height={16} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Array.from({ length: group.cards }).map((_, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 1,
                  minHeight: 56,
                  flex: "1 1 148px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderColor: "divider",
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={32}
                  height={32}
                  sx={{ borderRadius: 1 }}
                />
                <Stack spacing={0.25} sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={28} height={24} />
                  <Skeleton variant="text" width="80%" height={14} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function SearchToolbarSkeleton() {
  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Skeleton
          variant="rounded"
          height={40}
          sx={{ maxWidth: { sm: 420 }, borderRadius: 1 }}
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.25,
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={220} height={24} />
          </Stack>
          <Skeleton
            variant="rounded"
            width={200}
            height={40}
            sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
          />
        </Stack>
      </Paper>
    </>
  );
}

function TableSkeleton() {
  return (
    <Stack spacing={0}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          ...statsTableContainerSx,
          borderColor: "primary.light",
        }}
      >
        <Table size="medium" sx={statsTableSx}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" width={48} sx={statsTableHeadCellSx} />
              <TableCell sx={statsTableHeadCellSx}>Paciente</TableCell>
              <TableCell sx={statsTableHeadCellSx}>CPF</TableCell>
              <TableCell sx={statsTableHeadCellSx}>Transação</TableCell>
              <TableCell sx={statsTableHeadCellSx} width={170}>
                Status
              </TableCell>
              <TableCell sx={statsTableHeadCellSx}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <TableRow key={index}>
                <TableCell padding="checkbox">
                  <Skeleton variant="circular" width={20} height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width="72%" />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={100} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={88} />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="rounded"
                    width={120}
                    height={26}
                    sx={{ borderRadius: 8 }}
                  />
                </TableCell>
                <TableCell>
                  <Skeleton variant="rounded" width={96} height={36} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Skeleton variant="rounded" height={52} sx={{ mt: 0.5, borderRadius: 1 }} />
    </Stack>
  );
}

export default function EcommercePatientsListSkeleton() {
  return (
    <Stack spacing={3}>
      <QuickStatsSkeleton />
      <SearchToolbarSkeleton />
      <TableSkeleton />
    </Stack>
  );
}
